import { LeftRight } from "@/components/LeftRight.tsx";
import { WModalButton } from "@/components/button/WToolButton.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import {
    type SectionWithItems,
    WSection,
    WSectionHeader,
    WSectionItems,
    WSelect,
} from "@/components/forms/WSelect.tsx";
import { controllerProps } from "@/components/forms/ariaController.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { availableCustomImagesAtom } from "@/modules/compute/customImagesQuery.ts";
import { startVmInRescueMutation, type VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import { type DiskWithType, privateDiskQueryAtom } from "@/modules/disks/disksListQuery.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { VmCustomImage } from "@warrenio/api-spec/spec.oats.gen";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

type BootableImage = DiskWithType | VmCustomImage;

interface ImageGroup {
    code: string;
    title: string;
    items: BootableImage[];
}

function IsoBootIcon() {
    return <MaskIcon className="size-1.125rem mask-icon inline-block jp-disc-icon text-primary" />;
}

interface BootFromIsoInputs {
    boot_image_uuid: string;
}

export function BootFromIsoModalContent({ vm: { uuid, location, status } }: { vm: VirtualMachineLoc }) {
    //#region Hooks
    const { t } = useLingui();

    const startInRescueMutation = useStandardMutation(startVmInRescueMutation);

    const allImages = useSuspenseQueryAtom(availableCustomImagesAtom);
    const allDisks = useSuspenseQueryAtom(privateDiskQueryAtom(location));

    const onSubmit: SubmitHandler<BootFromIsoInputs> = async (inputs) => {
        const { boot_image_uuid } = inputs;

        const isPlatform = allImages.find((item) => item.uuid === boot_image_uuid);

        await startInRescueMutation.mutateAsync({
            body: {
                uuid,
                boot_image_uuid,
                boot_image_repository: isPlatform ? "platform" : "private",
            },
            location,
        });
        // TODO: start VM console
    };

    const form = useForm<BootFromIsoInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            boot_image_uuid: allImages[0]?.uuid,
        },
    });
    //#endregion

    const groups: ImageGroup[] = [
        { code: "private", title: t`Private images`, items: [...allDisks.values()] },
        { code: "platform", title: t`Platform provided`, items: allImages },
    ];

    const sections = groups
        .filter((group) => group.items.length)
        .map(({ code, title, items }): SectionWithItems<BootableImage> => {
            return {
                component: (
                    <WSection>
                        <WSectionHeader>
                            <LeftRight icon={<IsoBootIcon />} title={title} />
                        </WSectionHeader>

                        <WSectionItems />
                    </WSection>
                ),
                key: code,
                items,
            };
        });

    const { control } = form;

    return (
        <WModalForm
            title={t`Boot from ISO medium`}
            label={t`Start`}
            form={form}
            isActionDisabled={status === "running"}
            onSubmit={onSubmit}
            footerNotice={
                status === "running" ? t`Booting is only possible when the virtual machine is stopped.` : undefined
            }
            className="max-w-32rem"
        >
            <p className="pb-4">
                <Trans>
                    You can boot your virtual machine from a special image for system recovery or to install a fresh
                    operating system. When this mode is activated, a chosen image will be attached as a boot medium. You
                    can then use the Terminal to access your machine. This boot mode is enabled only for the current
                    launch. Next time this virtual machine is started it will follow the normal boot routine.
                </Trans>
            </p>

            <FormField wide headless isRequired label={t`Custom ISO image`}>
                <Controller
                    control={control}
                    name="boot_image_uuid"
                    {...controllerProps({ isRequired: true })}
                    render={({ field }) => (
                        <WSelect
                            aria-label="Select type"
                            sections={sections}
                            getKey={(item) => item.uuid}
                            getTextValue={(item) => item.uuid}
                            searchItems
                            onChange={(_iso, key) => field.onChange(key)}
                            valueKey={field.value}
                        >
                            {(item) =>
                                "description" in item && item.description
                                    ? item.description
                                    : "display_name" in item && item.display_name
                                      ? item.display_name
                                      : item.uuid
                            }
                        </WSelect>
                    )}
                />
            </FormField>
        </WModalForm>
    );
}

export function BootFromIsoModal({ vm }: { vm: VirtualMachineLoc }) {
    const { t } = useLingui();

    return (
        <WModal button={<WModalButton label={t`ISO boot`} icon="jp-disc-icon" />}>
            <BootFromIsoModalContent vm={vm} />
        </WModal>
    );
}
